import { Controller } from "@hotwired/stimulus";
import {
  addMeasureCompound,
  calculateMeasure,
  changeMeasureMode,
  editMeasure,
  emptyCompound,
  emptyMeasureState,
  formatInput,
  formatNumber,
  formatUnits,
  formatVolume,
  fromDisplay,
  MAX_COMPOUNDS,
  measureCompoundName,
  measureNumber,
  parseMeasureState,
  removeMeasureCompound,
  toDisplay,
} from "../reconstitution.js";

const MAX_UNITS = 100;
const ROW_SELECTOR = '[data-reconstitution-calculator-target~="compoundRow"]';
const DOSE_ROW_SELECTOR = '[data-reconstitution-calculator-target~="componentDoseRow"]';
const STORAGE_KEY = "calculator:measure";

export default class extends Controller {
  static targets = [
    "entryModeButton",
    "modeHelp",
    "quantityLabel",
    "volume",
    "volumeHint",
    "compoundList",
    "compoundTemplate",
    "compoundRow",
    "addCompound",
    "supplySummary",
    "totalDose",
    "totalDoseLabel",
    "totalDosePin",
    "totalDoseUnitButton",
    "componentDoseSection",
    "componentDoseList",
    "componentDoseTemplate",
    "componentDoseRow",
    "draw",
    "drawPin",
    "helper",
    "unitsOut",
    "volumeOut",
    "concentrationCard",
    "concentrationOut",
    "portionsCard",
    "portionsOut",
    "notice",
    "ruler",
  ];

  connect() {
    this.loadState(this.restoreState() ?? this.buildInitialState());
  }

  reset() {
    this.loadState(emptyMeasureState());
  }

  loadState(state) {
    this.state = state;
    this.compoundListTarget.replaceChildren();
    this.state.compounds.forEach((compound) => this.appendCompoundRow(compound));
    this.volumeTarget.value = formatInput(this.state.volumeMl);
    this.rewriteAmountInputs();
    this.render();
  }

  restoreState() {
    const snapshot = parseMeasureState(this.element.dataset.measureState);
    if (snapshot) return snapshot;

    try {
      return parseMeasureState(window.localStorage.getItem(STORAGE_KEY));
    } catch {
      return null;
    }
  }

  saveState() {
    const saved = JSON.stringify({ version: 1, state: this.state });
    this.element.dataset.measureState = saved;

    try {
      window.localStorage.setItem(STORAGE_KEY, saved);
    } catch {
      return;
    }
  }

  buildInitialState() {
    const activeMode = this.entryModeButtonTargets.find((button) => button.dataset.active === "true");
    const doseUnit = this.activeUnit(this.totalDoseUnitButtonTargets, "mcg");

    const compounds = this.compoundRowTargets.map((row) => {
      const unit = this.rowUnit(row);
      return {
        id: row.dataset.id,
        quantity: fromDisplay(measureNumber(this.rowPart(row, "amount").value), unit),
        unit,
        doseUnit,
      };
    });

    return {
      entryMode: activeMode ? activeMode.dataset.value : "amount",
      volumeMl: fromDisplay(measureNumber(this.volumeTarget.value), "mL"),
      compounds: compounds.length > 0 ? compounds : [emptyCompound("compound-1")],
      target: { kind: "total", value: fromDisplay(measureNumber(this.totalDoseTarget.value), doseUnit) },
      doseUnit,
    };
  }

  setEntryMode(event) {
    const next = changeMeasureMode(this.state, event.currentTarget.dataset.value);

    if (next === null) {
      const mode = event.currentTarget.dataset.value === "amount" ? "total amount" : "strength";
      this.modeHelpTarget.textContent = `Enter the amount of BAC water added to switch to ${mode}.`;
      this.volumeHintTarget.textContent = "";
      this.volumeTarget.focus();
      return;
    }

    this.state = next;
    this.rewriteAmountInputs();
    this.render();
  }

  editVolume() {
    this.state = editMeasure(this.state, { kind: "volume" }, this.volumeTarget.value, "mL");
    this.render();
  }

  editAmount(event) {
    const row = event.currentTarget.closest(ROW_SELECTOR);
    this.state = editMeasure(
      this.state,
      { kind: "quantity", id: row.dataset.id },
      event.currentTarget.value,
      this.rowUnit(row)
    );
    this.render();
  }

  setAmountUnit(event) {
    const row = event.currentTarget.closest(ROW_SELECTOR);
    const unit = event.currentTarget.dataset.value;
    this.state = {
      ...this.state,
      compounds: this.state.compounds.map((compound) =>
        compound.id === row.dataset.id ? { ...compound, unit } : compound
      ),
    };
    this.rewriteAmountInputs();
    this.render();
  }

  editTotalDose(event) {
    this.state = editMeasure(this.state, { kind: "total" }, event.currentTarget.value, this.state.doseUnit);
    this.render();
  }

  setTotalDoseUnit(event) {
    const unit = event.currentTarget.dataset.value;
    this.state = { ...this.state, doseUnit: unit };
    this.render();
  }

  editComponentDose(event) {
    const row = event.currentTarget.closest(DOSE_ROW_SELECTOR);
    this.state = editMeasure(
      this.state,
      { kind: "component", id: row.dataset.id },
      event.currentTarget.value,
      this.doseRowUnit(row)
    );
    this.render();
  }

  setComponentDoseUnit(event) {
    const row = event.currentTarget.closest(DOSE_ROW_SELECTOR);
    const unit = event.currentTarget.dataset.value;
    this.state = {
      ...this.state,
      compounds: this.state.compounds.map((compound) =>
        compound.id === row.dataset.id ? { ...compound, doseUnit: unit } : compound
      ),
    };
    this.render();
  }

  editDraw(event) {
    this.state = editMeasure(this.state, { kind: "draw" }, event.currentTarget.value, "units");
    this.render();
  }

  addCompound() {
    const before = this.state.compounds.length;
    this.state = addMeasureCompound(this.state);
    if (this.state.compounds.length === before) return;

    const compound = this.state.compounds[this.state.compounds.length - 1];
    this.appendCompoundRow(compound);

    this.render();
  }

  appendCompoundRow(compound) {
    const row = this.compoundTemplateTarget.content.firstElementChild.cloneNode(true);
    row.dataset.id = compound.id;
    row.dataset.unit = compound.unit;
    this.compoundListTarget.appendChild(row);
  }

  removeCompound(event) {
    const row = event.currentTarget.closest(ROW_SELECTOR);
    this.state = removeMeasureCompound(this.state, row.dataset.id);
    row.remove();
    this.render();
  }

  render() {
    const result = calculateMeasure(this.state);

    this.syncControls(result);
    this.renderReactiveFields(result);
    this.renderResults(result);
    this.renderHelper(result);
    this.saveState();
  }

  syncControls(result) {
    const strength = this.state.entryMode === "strength";
    const blend = this.state.compounds.length > 1;

    this.entryModeButtonTargets.forEach((button) => {
      button.dataset.active = String(button.dataset.value === this.state.entryMode);
      button.setAttribute("aria-pressed", button.dataset.active);
    });

    const quantityLabel = strength ? "Strength" : "Powder in vial";

    this.modeHelpTarget.textContent = strength
      ? "Enter the concentration per mL."
      : "Enter the total amount of powder in the vial.";
    this.quantityLabelTarget.textContent = quantityLabel;
    this.volumeHintTarget.textContent = strength ? "(optional)" : "";

    this.compoundRowTargets.forEach((row, index) => {
      const compound = this.compoundById(row.dataset.id);
      const unit = compound ? compound.unit : this.rowUnit(row);

      this.rowPart(row, "amount").setAttribute(
        "aria-label",
        blend ? `${measureCompoundName(compound, index)} ${strength ? "strength" : "amount"}` : quantityLabel
      );
      this.rowPart(row, "trailing").textContent = strength ? "/mL" : "";
      this.rowPart(row, "remove").hidden = !blend;
      row.querySelectorAll('[data-compound-part="unitButton"]').forEach((button) => {
        button.dataset.active = String(button.dataset.value === unit);
        button.setAttribute("aria-pressed", button.dataset.active);
      });
    });

    this.addCompoundTarget.hidden = this.state.compounds.length >= MAX_COMPOUNDS;
    this.totalDoseLabelTarget.textContent = blend ? "Total blend dose" : "Total dose";
    this.totalDoseUnitButtonTargets.forEach((button) => {
      button.dataset.active = String(button.dataset.value === this.state.doseUnit);
      button.setAttribute("aria-pressed", button.dataset.active);
    });
    this.totalDosePinTarget.hidden =
      this.state.target.kind !== "total" && !(this.state.target.kind === "component" && !blend);
    this.drawPinTarget.hidden = this.state.target.kind !== "volume";

    this.componentDoseSectionTarget.hidden = !blend;
    this.syncComponentDoseRows(blend ? this.state.compounds : []);

    this.renderSupplySummary(result);
  }

  renderSupplySummary(result) {
    if (!result.supplyReady) {
      this.supplySummaryTarget.hidden = true;
      return;
    }

    this.supplySummaryTarget.hidden = false;
    const concentration = `${formatNumber(result.concentration)} mg/mL`;
    this.supplySummaryTarget.textContent =
      result.totalInVial !== null
        ? `${concentration} · ${formatNumber(result.totalInVial)} mg in ${formatVolume(this.state.volumeMl)} mL`
        : concentration;
  }

  syncComponentDoseRows(compounds) {
    const existing = new Map(this.componentDoseRowTargets.map((row) => [row.dataset.id, row]));

    compounds.forEach((compound) => {
      if (!existing.has(compound.id)) {
        const row = this.componentDoseTemplateTarget.content.firstElementChild.cloneNode(true);
        row.dataset.id = compound.id;
        this.componentDoseListTarget.appendChild(row);
      }
      existing.delete(compound.id);
    });

    existing.forEach((row) => row.remove());
  }

  renderReactiveFields(result) {
    const target = this.state.target;
    const totalPinned = target.kind === "total" || (target.kind === "component" && this.state.compounds.length === 1);
    const totalDose = totalPinned ? target.value : result.totalDose;
    const drawVolume = target.kind === "volume" ? target.value : result.volumeMl;

    this.writeField(this.totalDoseTarget, formatInput(toDisplay(totalDose, this.state.doseUnit)));

    this.writeField(this.drawTarget, formatInput(toDisplay(drawVolume, "units")));

    this.componentDoseRowTargets.forEach((row) => {
      const index = this.state.compounds.findIndex((compound) => compound.id === row.dataset.id);
      const compound = index >= 0 ? this.state.compounds[index] : null;

      const name = measureCompoundName(compound, Math.max(index, 0));

      this.doseRowPart(row, "label").textContent = name;
      this.doseRowPart(row, "dose").setAttribute("aria-label", `${name} dose`);
      const pinned = target.kind === "component" && target.id === row.dataset.id;
      this.doseRowPart(row, "pin").hidden = !pinned;
      row.querySelectorAll('[data-dose-part="unitButton"]').forEach((button) => {
        button.dataset.active = String(button.dataset.value === (compound ? compound.doseUnit : "mg"));
        button.setAttribute("aria-pressed", button.dataset.active);
      });

      const dose = pinned ? target.value : result.doses[index];
      this.writeField(
        this.doseRowPart(row, "dose"),
        compound && dose !== undefined ? formatInput(toDisplay(dose, compound.doseUnit)) : ""
      );
    });
  }

  writeField(element, value) {
    if (element === document.activeElement) return;
    element.value = value;
  }

  renderResults(result) {
    this.concentrationCardTarget.hidden = !result.supplyReady;
    if (result.supplyReady) {
      this.concentrationOutTarget.textContent = `${formatNumber(result.concentration)} mg/mL`;
    }

    if (result.ready) {
      this.unitsOutTarget.textContent = `${formatUnits(result.syringeUnits)} units`;
      this.volumeOutTarget.textContent = `${formatVolume(result.volumeMl)} mL`;
      this.portionsCardTarget.hidden = result.fullDoses === null;
      if (result.fullDoses !== null) {
        this.portionsOutTarget.textContent = `${formatNumber(result.fullDoses)} ${result.fullDoses === 1 ? "dose" : "doses"}`;
      }
    } else {
      this.unitsOutTarget.textContent = "No draw calculated";
      this.volumeOutTarget.textContent = "";
      this.portionsCardTarget.hidden = true;
    }

    this.notice(result);
    this.renderRuler(result.syringeUnits);
  }

  notice(result) {
    let message = null;
    if (result.exceedsSyringe && result.exceedsVial) {
      message = `This draw is more than the 1 mL syringe holds and more than the ${formatVolume(this.state.volumeMl)} mL in the vial.`;
    } else if (result.exceedsSyringe) {
      message = "This draw is more than a 1 mL U-100 syringe holds.";
    } else if (result.exceedsVial) {
      message = `This draw is more than the ${formatVolume(this.state.volumeMl)} mL in the vial.`;
    }

    this.noticeTarget.hidden = message === null;
    if (message !== null) this.noticeTarget.textContent = message;
  }

  renderHelper(result) {
    if (!result.supplyReady) {
      this.helperTarget.textContent =
        this.state.entryMode === "strength"
          ? "Enter the strength to see the draw."
          : "Enter the amount and volume to see the draw.";
      return;
    }

    if (!result.ready) {
      this.helperTarget.textContent = "Enter a dose or a draw amount to see the draw.";
      return;
    }

    if (this.state.target.kind === "volume") {
      this.helperTarget.textContent = `Your draw stays at ${formatInput(toDisplay(this.state.target.value, "units"))} units as you change the vial.`;
    } else if (this.state.target.kind === "component") {
      const compound = this.compoundById(this.state.target.id);
      const index = this.state.compounds.indexOf(compound);
      const name = measureCompoundName(compound, index);
      this.helperTarget.textContent = `Your ${name} dose stays at ${formatInput(toDisplay(this.state.target.value, compound.doseUnit))} ${compound.doseUnit} as you change the vial.`;
    } else {
      this.helperTarget.textContent = `Your total dose stays at ${formatInput(toDisplay(this.state.target.value, this.state.doseUnit))} ${this.state.doseUnit} as you change the vial.`;
    }
  }

  renderRuler(units) {
    const clampedUnits = Math.max(0, Math.min(MAX_UNITS, units));
    const trackX0 = 6;
    const trackX1 = 594;
    const baseY = 32;
    const xForUnit = (unit) => trackX0 + (unit / MAX_UNITS) * (trackX1 - trackX0);

    const ticks = [];
    for (let unit = 0; unit <= MAX_UNITS; unit += 2) {
      const major = unit % 10 === 0;
      const endCap = unit === 0 || unit === MAX_UNITS;
      const x = xForUnit(unit).toFixed(1);
      ticks.push(
        `<line x1="${x}" y1="${baseY}" x2="${x}" y2="${major ? 6 : 18}" stroke="var(--color-ink-subtle)" stroke-width="${endCap ? 1.1 : major ? 0.9 : 0.7}" opacity="${major ? 0.9 : 0.6}"></line>`
      );
    }

    const markerX = xForUnit(clampedUnits).toFixed(1);
    const fill =
      clampedUnits > 0
        ? `<rect x="${trackX0}" y="6" width="${(xForUnit(clampedUnits) - trackX0).toFixed(1)}" height="${baseY - 6}" fill="var(--color-dawn-1)" opacity="0.08"></rect>`
        : "";

    this.rulerTarget.innerHTML = `
      ${fill}
      <line x1="${trackX0}" y1="${baseY}" x2="${trackX1}" y2="${baseY}" stroke="var(--color-ink-subtle)" stroke-width="1" opacity="0.9"></line>
      ${ticks.join("")}
      <line x1="${markerX}" y1="2" x2="${markerX}" y2="38" stroke="var(--color-dawn-1)" stroke-width="2.5" stroke-linecap="square"></line>
    `;
  }

  rewriteAmountInputs() {
    this.compoundRowTargets.forEach((row) => {
      const compound = this.compoundById(row.dataset.id);
      if (compound) this.rowPart(row, "amount").value = formatInput(toDisplay(compound.quantity, compound.unit));
    });
  }

  compoundById(id) {
    return this.state.compounds.find((compound) => compound.id === id) ?? null;
  }

  rowUnit(row) {
    const active = row.querySelector('[data-compound-part="unitButton"][data-active="true"]');
    return active ? active.dataset.value : (row.dataset.unit ?? "mg");
  }

  doseRowUnit(row) {
    const compound = this.compoundById(row.dataset.id);
    return compound ? compound.doseUnit : "mg";
  }

  activeUnit(buttons, fallback) {
    const active = buttons.find((button) => button.dataset.active === "true");
    return active ? active.dataset.value : fallback;
  }

  rowPart(row, part) {
    return row.querySelector(`[data-compound-part="${part}"]`);
  }

  doseRowPart(row, part) {
    return row.querySelector(`[data-dose-part="${part}"]`);
  }
}
